(() => {
  const round = (n, digits = 2) => Number(n.toFixed(digits));
  const fmt = (n, digits = 2) => new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(round(n, digits));
  const finitePositive = (n) => Number.isFinite(n) && n > 0;
  const finiteNonNegative = (n) => Number.isFinite(n) && n >= 0;

  function smartVolume(ml) {
    if (ml >= 1000) return `${fmt(ml)} ml (${fmt(ml / 1000)} L)`;
    return `${fmt(ml)} ml`;
  }

  function smartMass(g) {
    if (g >= 1000) return `${fmt(g)} g (${fmt(g / 1000)} kg)`;
    return `${fmt(g)} g`;
  }

  function resultRow(label, value, note = '', total = false) {
    const row = document.createElement('div');
    row.className = `yf-calc__result-row${total ? ' is-total' : ''}`;
    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    const valueEl = document.createElement('strong');
    valueEl.textContent = value;
    if (note) {
      const noteEl = document.createElement('small');
      noteEl.textContent = note;
      valueEl.appendChild(noteEl);
    }
    row.append(labelEl, valueEl);
    return row;
  }

  function replaceRows(container, rows) {
    container.replaceChildren(...rows);
  }

  function initCalculator(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const toolButtons = [...root.querySelectorAll('[data-yf-tool]')];
    const panels = [...root.querySelectorAll('[data-yf-panel]')];

    function setTool(tool) {
      toolButtons.forEach((button) => {
        const active = button.dataset.yfTool === tool;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.yfPanel !== tool; });
    }

    toolButtons.forEach((button) => button.addEventListener('click', () => setTool(button.dataset.yfTool)));

    // Batch calculator
    const batchForm = root.querySelector('[data-yf-batch-form]');
    const formatButtons = [...root.querySelectorAll('[data-yf-format]')];
    const batchSize = root.querySelector('[data-yf-batch-size]');
    const batchQuantity = root.querySelector('[data-yf-batch-quantity]');
    const batchFragrance = root.querySelector('[data-yf-batch-fragrance]');
    const batchWater = root.querySelector('[data-yf-batch-water]');
    const waterField = root.querySelector('[data-yf-water-field]');
    const batchCosolvent = root.querySelector('[data-yf-batch-cosolvent]');
    const batchReset = root.querySelector('[data-yf-batch-reset]');
    const batchError = root.querySelector('[data-yf-batch-error]');
    const batchResults = root.querySelector('[data-yf-batch-results]');
    const batchTitle = root.querySelector('[data-yf-batch-title]');
    const batchEquation = root.querySelector('[data-yf-batch-equation]');
    const perBottle = root.querySelector('[data-yf-per-bottle]');
    let format = 'alcohol';
    let previousWater = 5;

    const batchDefaults = { size: 100, quantity: 1, fragrance: 20, water: 5, cosolvent: 0 };

    function setFormat(next) {
      if (format === 'alcohol') previousWater = Number(batchWater.value) || 0;
      format = next;
      formatButtons.forEach((button) => {
        const active = button.dataset.yfFormat === format;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      waterField.hidden = format !== 'alcohol';
      batchWater.value = format === 'alcohol' ? previousWater : 0;
      calculateBatch();
    }

    function calculateBatch(event) {
      if (event) event.preventDefault();
      batchError.hidden = true;
      batchError.textContent = '';

      const bottleSize = Number(batchSize.value);
      const units = Number(batchQuantity.value);
      const fragrancePct = Number(batchFragrance.value);
      const waterPct = format === 'alcohol' ? Number(batchWater.value) : 0;
      const cosolventPct = Number(batchCosolvent.value);

      if (!finitePositive(bottleSize) || !finitePositive(units) || !Number.isInteger(units) || !finitePositive(fragrancePct) || !finiteNonNegative(waterPct) || !finiteNonNegative(cosolventPct)) {
        batchError.textContent = 'Enter valid values. The bottle count must be a whole number; water and co-solvent may be 0.';
        batchError.hidden = false;
        return;
      }

      const usedPct = fragrancePct + waterPct + cosolventPct;
      if (usedPct >= 100) {
        batchError.textContent = 'Fragrance, water and co-solvent must total less than 100% so there is room for the main base.';
        batchError.hidden = false;
        return;
      }

      const total = bottleSize * units;
      if (!Number.isFinite(total) || total > 1e12) {
        batchError.textContent = 'The batch is too large for this browser calculator. Use a smaller value or production software.';
        batchError.hidden = false;
        return;
      }

      const fragranceMl = total * fragrancePct / 100;
      const waterMl = total * waterPct / 100;
      const cosolventMl = total * cosolventPct / 100;
      const mainBaseMl = total - fragranceMl - waterMl - cosolventMl;
      const perBottleFragrance = bottleSize * fragrancePct / 100;
      const perBottleWater = bottleSize * waterPct / 100;
      const perBottleCosolvent = bottleSize * cosolventPct / 100;
      const perBottleBase = bottleSize - perBottleFragrance - perBottleWater - perBottleCosolvent;

      const rows = [resultRow('Perfume fragrance oil', smartVolume(fragranceMl))];
      if (format === 'alcohol') {
        rows.push(resultRow('Perfumery alcohol', smartVolume(mainBaseMl)));
        if (waterPct > 0) rows.push(resultRow('Purified water', smartVolume(waterMl)));
      } else {
        rows.push(resultRow('Carrier oil / tested oil base', smartVolume(mainBaseMl)));
      }
      if (cosolventMl > 0) rows.push(resultRow('Optional co-solvent', smartVolume(cosolventMl)));
      rows.push(resultRow('Theoretical batch volume', smartVolume(total), '', true));
      replaceRows(batchResults, rows);

      const formatName = format === 'alcohol' ? 'alcohol-based' : 'oil-based';
      batchTitle.textContent = `Your ${smartVolume(total)} ${formatName} formula`;
      perBottle.textContent = units > 1
        ? `Per bottle: ${fmt(perBottleFragrance)} ml fragrance + ${fmt(perBottleBase)} ml ${format === 'alcohol' ? 'alcohol' : 'carrier base'}${perBottleWater > 0 ? ` + ${fmt(perBottleWater)} ml water` : ''}${perBottleCosolvent > 0 ? ` + ${fmt(perBottleCosolvent)} ml co-solvent` : ''}.`
        : '';

      const parts = [`${fmt(fragranceMl)} ml fragrance oil`, `${fmt(mainBaseMl)} ml ${format === 'alcohol' ? 'perfumery alcohol' : 'carrier base'}`];
      if (waterMl > 0) parts.push(`${fmt(waterMl)} ml purified water`);
      if (cosolventMl > 0) parts.push(`${fmt(cosolventMl)} ml co-solvent`);
      batchEquation.textContent = `${parts.join(' + ')} = ${fmt(total)} ml theoretical batch`;
    }

    formatButtons.forEach((button) => button.addEventListener('click', () => setFormat(button.dataset.yfFormat)));
    batchForm.addEventListener('submit', calculateBatch);
    batchReset.addEventListener('click', () => {
      batchSize.value = batchDefaults.size;
      batchQuantity.value = batchDefaults.quantity;
      batchFragrance.value = batchDefaults.fragrance;
      batchWater.value = batchDefaults.water;
      previousWater = batchDefaults.water;
      batchCosolvent.value = batchDefaults.cosolvent;
      setFormat('alcohol');
    });

    // Yield calculator
    const yieldForm = root.querySelector('[data-yf-yield-form]');
    const basisButtons = [...root.querySelectorAll('[data-yf-basis]')];
    const available = root.querySelector('[data-yf-available]');
    const availableUnit = root.querySelector('[data-yf-available-unit]');
    const yieldFragrance = root.querySelector('[data-yf-yield-fragrance]');
    const yieldBottleSize = root.querySelector('[data-yf-yield-bottle-size]');
    const fillWeight = root.querySelector('[data-yf-fill-weight]');
    const density = root.querySelector('[data-yf-density]');
    const loss = root.querySelector('[data-yf-loss]');
    const price = root.querySelector('[data-yf-price]');
    const volumeBottleField = root.querySelector('[data-yf-volume-bottle-field]');
    const weightFillField = root.querySelector('[data-yf-weight-fill-field]');
    const densityField = root.querySelector('[data-yf-density-field]');
    const yieldReset = root.querySelector('[data-yf-yield-reset]');
    const yieldError = root.querySelector('[data-yf-yield-error]');
    const yieldResults = root.querySelector('[data-yf-yield-results]');
    const yieldTitle = root.querySelector('[data-yf-yield-title]');
    const yieldEquation = root.querySelector('[data-yf-yield-equation]');
    let basis = 'volume';

    const yieldDefaults = { available: 1, unit: 'kg', fragrance: 20, bottleSize: 50, fillWeight: 50, density: 1, loss: 0, price: '' };

    function setBasis(next) {
      basis = next;
      basisButtons.forEach((button) => {
        const active = button.dataset.yfBasis === basis;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      volumeBottleField.hidden = basis !== 'volume';
      densityField.hidden = basis !== 'volume';
      weightFillField.hidden = basis !== 'weight';
      calculateYield();
    }

    function calculateYield(event) {
      if (event) event.preventDefault();
      yieldError.hidden = true;
      yieldError.textContent = '';

      const availableInput = Number(available.value);
      const availableG = availableInput * (availableUnit.value === 'kg' ? 1000 : 1);
      const fragrancePct = Number(yieldFragrance.value);
      const lossPct = Number(loss.value);
      const pricePerKg = price.value.trim() === '' ? null : Number(price.value);

      if (!finitePositive(availableInput) || !finitePositive(fragrancePct) || !finiteNonNegative(lossPct) || lossPct >= 100 || (pricePerKg !== null && !finiteNonNegative(pricePerKg))) {
        yieldError.textContent = 'Enter valid values. Process loss must be between 0% and less than 100%; price may be left blank.';
        yieldError.hidden = false;
        return;
      }

      const usableG = availableG * (1 - lossPct / 100);
      let fragrancePerBottleG;
      let fragrancePerBottleDisplay;
      let exactBeforeLoss;
      let exactAfterLoss;
      let batchDisplay;
      let bottleLabel;
      const rows = [];

      if (basis === 'volume') {
        const bottleMl = Number(yieldBottleSize.value);
        const densityValue = Number(density.value);
        if (!finitePositive(bottleMl) || !finitePositive(densityValue)) {
          yieldError.textContent = 'Bottle size and fragrance density must be greater than 0.';
          yieldError.hidden = false;
          return;
        }
        const fragrancePerBottleMl = bottleMl * fragrancePct / 100;
        fragrancePerBottleG = fragrancePerBottleMl * densityValue;
        fragrancePerBottleDisplay = `${fmt(fragrancePerBottleMl)} ml (${fmt(fragrancePerBottleG)} g at the entered density)`;
        exactBeforeLoss = availableG / fragrancePerBottleG;
        exactAfterLoss = usableG / fragrancePerBottleG;
        const whole = Math.floor(exactAfterLoss + 1e-9);
        batchDisplay = smartVolume(whole * bottleMl);
        bottleLabel = `${fmt(bottleMl)} ml`;
        rows.push(resultRow('Estimated fragrance volume', smartVolume(availableG / densityValue), `Using ${fmt(densityValue)} g/ml`));
        rows.push(resultRow('Fragrance needed per bottle', fragrancePerBottleDisplay));
        rows.push(resultRow('Theoretical bottles before loss', fmt(exactBeforeLoss)));
        rows.push(resultRow('Practical whole bottles after loss', fmt(whole, 0), `${fmt(exactAfterLoss)} calculated before rounding down`, true));
        rows.push(resultRow('Finished batch represented', batchDisplay));
        yieldTitle.textContent = `About ${fmt(whole, 0)} whole ${bottleLabel} bottles`;
        yieldEquation.textContent = `${smartMass(usableG)} usable fragrance ÷ ${fmt(fragrancePerBottleG)} g fragrance per bottle = ${fmt(exactAfterLoss)} bottles before rounding down`;
      } else {
        const fillG = Number(fillWeight.value);
        if (!finitePositive(fillG)) {
          yieldError.textContent = 'Actual finished fill weight must be greater than 0.';
          yieldError.hidden = false;
          return;
        }
        fragrancePerBottleG = fillG * fragrancePct / 100;
        fragrancePerBottleDisplay = smartMass(fragrancePerBottleG);
        exactBeforeLoss = availableG / fragrancePerBottleG;
        exactAfterLoss = usableG / fragrancePerBottleG;
        const whole = Math.floor(exactAfterLoss + 1e-9);
        batchDisplay = smartMass(whole * fillG);
        bottleLabel = `${fmt(fillG)} g fill`;
        rows.push(resultRow('Fragrance needed per bottle', fragrancePerBottleDisplay));
        rows.push(resultRow('Theoretical bottles before loss', fmt(exactBeforeLoss)));
        rows.push(resultRow('Practical whole bottles after loss', fmt(whole, 0), `${fmt(exactAfterLoss)} calculated before rounding down`, true));
        rows.push(resultRow('Finished batch represented', batchDisplay));
        yieldTitle.textContent = `About ${fmt(whole, 0)} whole bottles at ${bottleLabel}`;
        yieldEquation.textContent = `${smartMass(usableG)} usable fragrance ÷ ${fmt(fragrancePerBottleG)} g fragrance per bottle = ${fmt(exactAfterLoss)} bottles before rounding down`;
      }

      if (pricePerKg !== null) {
        const costPerBottle = pricePerKg * fragrancePerBottleG / 1000;
        rows.push(resultRow('Fragrance cost per bottle', fmt(costPerBottle), 'In the currency entered for price per kg'));
      }
      replaceRows(yieldResults, rows);
    }

    basisButtons.forEach((button) => button.addEventListener('click', () => setBasis(button.dataset.yfBasis)));
    yieldForm.addEventListener('submit', calculateYield);
    yieldReset.addEventListener('click', () => {
      available.value = yieldDefaults.available;
      availableUnit.value = yieldDefaults.unit;
      yieldFragrance.value = yieldDefaults.fragrance;
      yieldBottleSize.value = yieldDefaults.bottleSize;
      fillWeight.value = yieldDefaults.fillWeight;
      density.value = yieldDefaults.density;
      loss.value = yieldDefaults.loss;
      price.value = yieldDefaults.price;
      setBasis('volume');
    });

    calculateBatch();
    calculateYield();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-yf-perfume-calculator-v2]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
